import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import axios from 'axios'
import { getMapboxCountry } from 'utils/mapboxLocation'
import plannedSternaPin from 'assets/plannedSternaPin'

import { Formik, useFormikContext } from 'formik'
import { useQueryClient } from '@tanstack/react-query'

import { useItineraries } from 'contexts/itinerary.context'

import { ModelItineraries } from 'models/ModelItineraries'

import PageContainer from 'components/page-container'

import { PainelCities, PainelDescription, PainelFace } from './componentes'
import {
  ContainerCreateItinerary,
  CoverActions,
  CoverButton,
  CoverCreateItinerary,
  CoverFileInput,
  CoverHint,
  CoverIcon,
  CoverOverlay,
  CoverRemoveButton,
  CoverTitle,
  FormCreateItinerary,
  FormContainerCreateItinerary,
  SavingCard,
  SavingOverlay,
  SavingSpinner
} from './style'

const DEFAULT_ITINERARY_COVER = '/images/default_itinerary_cover.svg'

const MAPBOX_GEOCODE_TOKEN =
  'pk.eyJ1IjoibGF1cmFoZGV2IiwiYSI6ImNsY3BtMTN4dDFueG0zcG81em41YXh4emIifQ.eGKakvFNW6mcmRuHze23mg'

// Resolve generated destinations through the same geographic source used by
// the editor. Use source coordinates to reject unrelated namesakes.
const reconcileGeneratedCities = async (dayPlan: any[]) => {
  const cache = new Map<string, any>()
  const inFlight = new Map<string, Promise<any>>()
  const radians = (n: number) => (n * Math.PI) / 180
  const kilometers = (
    latA: number,
    lonA: number,
    latB: number,
    lonB: number
  ) => {
    const deltaLat = radians(latB - latA)
    const deltaLon = radians(lonB - lonA)
    const a =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(radians(latA)) *
        Math.cos(radians(latB)) *
        Math.sin(deltaLon / 2) ** 2
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }
  const resolve = async (city: any) => {
    const originalName = String(city?.name || '').trim()
    const lat = Number(city?.latitude)
    const lon = Number(city?.longitude)
    const cacheKey =
      originalName.toLocaleLowerCase('pt-BR') + ':' + lat + ':' + lon
    if (cache.has(cacheKey)) return { ...city, ...cache.get(cacheKey) }
    if (inFlight.has(cacheKey)) {
      const patch = await inFlight.get(cacheKey)
      return patch ? { ...city, ...patch } : city
    }
    if (
      !originalName ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lon) ||
      (lat === 0 && lon === 0)
    )
      return city
    const lookup = async () => {
      try {
        const response = await axios.get(
          'https://api.mapbox.com/search/geocode/v6/forward',
          {
            params: {
              q: originalName,
              types: 'place,locality',
              language: 'pt,de,en',
              autocomplete: false,
              limit: 8,
              access_token: MAPBOX_GEOCODE_TOKEN
            },
            timeout: 9000
          }
        )
        const candidates: any[] = response.data?.features || []
        const closest = candidates
          .map((feature) => {
            const coordinates = feature?.geometry?.coordinates || []
            const pointLon = Number(coordinates[0])
            const pointLat = Number(coordinates[1])
            return {
              feature,
              distance:
                Number.isFinite(pointLat) && Number.isFinite(pointLon)
                  ? kilometers(lat, lon, pointLat, pointLon)
                  : Infinity
            }
          })
          .sort((a, b) => a.distance - b.distance)[0]
        if (closest && closest.distance <= 15) {
          const selected = closest.feature
          const { countryName, countryCode } = getMapboxCountry(selected)
          // Preserve the requested/canonical display identity. Mapbox may return
          // a shortened locality label (e.g. Civita instead of Civita di Bagnoregio).
          const name = originalName
          const patch = {
            name,
            latitude: Number(selected.geometry.coordinates[1]),
            longitude: Number(selected.geometry.coordinates[0]),
            countryName: countryName || city.countryName || '',
            countryCode: countryCode || city.countryCode || ''
          }
          cache.set(cacheKey, patch)
          return patch
        }
        console.warn('[inspiration-draft] no nearby Mapbox locality', {
          name: originalName
        })
      } catch (error) {
        console.warn('[inspiration-draft] Mapbox reconciliation unavailable', {
          name: originalName
        })
      }
      return null
    }
    const pending = lookup()
    inFlight.set(cacheKey, pending)
    try {
      const patch = await pending
      return patch ? { ...city, ...patch } : city
    } finally {
      inFlight.delete(cacheKey)
    }
  }
  return Promise.all(
    dayPlan.map(async (day) => ({
      ...day,
      cities: await Promise.all((day.cities || []).map(resolve))
    }))
  )
}

const emptyCreateCity = () => ({
  id: '',
  name: '',
  description: '',
  latitude: 0,
  longitude: 0,
  images: [],
  accommodations: []
})

const initialDayPlan = () => [
  {
    dayNumber: 1,
    date: null,
    title: '',
    cities: [emptyCreateCity()]
  }
]

const addDaysToDate = (dateValue: string | null, offset: number) => {
  if (!dateValue) return null

  const [year, month, day] = dateValue.split('-').map(Number)
  const date = new Date(year, month - 1, day + offset)

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-')
}

const formEmptyValues: ModelItineraries = {
  id: '',
  made: 'true',
  title: '',
  tripDate: '',
  coverImage: '',
  summary: '',
  days: 1,
  spent: 0.0,
  type: 'simple',
  authorId: '',
  interests: [],
  collaboratorIds: [],
  cities: [],
  dayPlan: initialDayPlan()
}

const FormikObserver = () => {
  const { values } = useFormikContext()

  React.useEffect(() => {
    window.localStorage.setItem('itinerary', JSON.stringify(values))
  }, [values])

  return null
}

const isMadeItinerary = (made: ModelItineraries['made']) =>
  made === true || String(made) === 'true'

const isPastTripDate = (tripDate?: string | null) => {
  if (!tripDate) return false

  const today = new Date()
  const localToday = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0')
  ].join('-')

  return tripDate < localToday
}

const getMissingRequiredFields = (values: ModelItineraries) => {
  const missing: string[] = []

  if (!values.title?.trim()) missing.push('título da viagem')
  if (isMadeItinerary(values.made) && !values.tripDate) {
    missing.push('data da viagem')
  }

  const dayPlan = Array.isArray(values.dayPlan) ? values.dayPlan : []
  const requestedDays = Number(values.days || 0)

  if (
    !Number.isInteger(requestedDays) ||
    requestedDays < 1 ||
    dayPlan.length !== requestedDays
  ) {
    missing.push('a estrutura completa dos dias')
  }

  const invalidDayIndex = dayPlan.findIndex(
    (day: any) =>
      !(day?.cities || []).some((city: any) => String(city?.name || '').trim())
  )

  if (invalidDayIndex >= 0) {
    missing.push(`ao menos uma cidade no Dia ${invalidDayIndex + 1}`)
  }

  return missing
}

const showMissingFieldsMessage = (missing: string[]) => {
  if (!missing.length) return

  const formatted =
    missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')} e ${missing[missing.length - 1]}`

  toast.warning(`Antes de salvar, preencha: ${formatted}.`, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
    theme: 'dark'
  })
}

const CreateItinerary = () => {
  const history = useHistory()
  const queryClient = useQueryClient()

  const { createItinerary, uploadFile } = useItineraries()

  const [creationMode, setCreationMode] = useState<
    'choose' | 'manual' | 'inspiration'
  >('choose')
  const [destinations, setDestinations] = useState('')
  const [travelDays, setTravelDays] = useState(5)
  const [travelInterests, setTravelInterests] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [draft, setDraft] = useState<ModelItineraries | null>(null)
  const [step, setStep] = useState(1)
  const [isUploadingCover, setIsUploadingCover] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Same monotonic, elapsed-time estimate as "Torne esse roteiro meu".
  useEffect(() => {
    if (!generating) return undefined
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      const seconds = (Date.now() - startedAt) / 1000
      const estimate =
        seconds <= 8
          ? 7 + (seconds / 8) * 33
          : seconds <= 20
            ? 40 + ((seconds - 8) / 12) * 28
            : seconds <= 40
              ? 68 + ((seconds - 20) / 20) * 20
              : seconds <= 70
                ? 88 + ((seconds - 40) / 30) * 7
                : 95
      setProgress((current) =>
        Math.max(current, Math.min(95, Math.round(estimate)))
      )
    }, 500)
    return () => window.clearInterval(timer)
  }, [generating])

  const nextStep = useCallback(() => {
    setStep(step + 1)
  }, [step])

  const prevStep = useCallback(() => {
    setStep(step - 1)
  }, [step])

  const uploadFileCallback = useCallback(
    async (file: File) => {
      const lastDot = file.name.lastIndexOf('.')
      const name = lastDot > 0 ? file.name.substring(0, lastDot) : file.name
      const ext = lastDot > 0 ? file.name.substring(lastDot + 1) : ''
      const noSpecialChars = name.replace(/[^\w\s-]/gi, '')
      const newName = ext ? `${noSpecialChars}.${ext}` : noSpecialChars

      const newFile = new File([file], newName, { type: file.type })
      return uploadFile(newFile, 'itinerary_cover')
    },
    [uploadFile]
  )

  const initialValues: ModelItineraries = useMemo(() => {
    if (draft) return draft
    const cached: string | null = window.localStorage.getItem('itinerary')
    if (!cached) return formEmptyValues

    try {
      const parsed = JSON.parse(cached)
      const requestedDays = Math.max(Number(parsed?.days) || 1, 1)

      if (Array.isArray(parsed?.dayPlan) && parsed.dayPlan.length > 0) {
        return {
          ...formEmptyValues,
          ...parsed,
          days: requestedDays,
          dayPlan: Array.from({ length: requestedDays }, (_, index) => {
            const existing = parsed.dayPlan[index]
            return (
              existing || {
                dayNumber: index + 1,
                date: null,
                title: '',
                cities: [emptyCreateCity()]
              }
            )
          }).map((day: any, index: number) => ({
            ...day,
            dayNumber: index + 1,
            cities:
              Array.isArray(day.cities) && day.cities.length > 0
                ? day.cities
                : [emptyCreateCity()]
          }))
        }
      }

      const legacyCities = Array.isArray(parsed?.cities)
        ? parsed.cities.filter((city: any) => city?.name)
        : []

      return {
        ...formEmptyValues,
        ...parsed,
        days: requestedDays,
        cities: [],
        dayPlan: Array.from({ length: requestedDays }, (_, index) => ({
          dayNumber: index + 1,
          date: null,
          title: '',
          cities:
            index === 0 && legacyCities.length > 0
              ? legacyCities
              : [emptyCreateCity()]
        }))
      }
    } catch {
      return formEmptyValues
    }
  }, [draft])

  const generateDraft = async () => {
    if (!destinations.trim()) {
      toast.warning(
        'Informe ao menos um destino, cidade ou lugar que deseja conhecer.'
      )
      return
    }
    if (travelInterests.trim().length < 5) {
      toast.warning(
        'Descreva o que gosta de fazer na viagem com pelo menos 5 caracteres.'
      )
      return
    }
    setGenerationError(null)
    setProgress(7)
    setGenerating(true)
    const requestId =
      'draft-' +
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2, 10)
    const startedAt = performance.now()
    const timings: Record<string, number | string> = {
      requestId,
      startedAt: new Date().toISOString()
    }
    const saveTelemetry = (status: string) => {
      try {
        const entry = {
          ...timings,
          status,
          totalMs: Math.round(performance.now() - startedAt)
        }
        const key = 'redsterna-inspiration-perf-v4'
        const previous = JSON.parse(window.sessionStorage.getItem(key) || '[]')
        window.sessionStorage.setItem(
          key,
          JSON.stringify(
            [...(Array.isArray(previous) ? previous : []), entry].slice(-10)
          )
        )
      } catch {
        // Telemetry is optional and must never interrupt generation.
      }
    }
    console.info('[inspiration-draft] client request started', {
      requestId,
      timestamp: new Date().toISOString()
    })
    try {
      // Clear any stale key from the experimental persisted-job flow.
      window.sessionStorage.removeItem('redsterna-inspiration-pending-job')
      const apiStartedAt = performance.now()
      const response = await api.post(
        '/itineraries/inspiration-draft',
        {
          destinations,
          days: travelDays,
          interests: travelInterests,
          requestId
        },
        { timeout: 180000 }
      )
      timings.apiMs = Math.round(performance.now() - apiStartedAt)
      timings.apiCompletedAtMs = Math.round(performance.now() - startedAt)
      console.info('[inspiration-draft] client API completed', {
        requestId,
        elapsedMs: timings.apiMs,
        sinceStartMs: timings.apiCompletedAtMs
      })
      const result = response?.data
      if (
        !result ||
        !Array.isArray(result.dayPlan) ||
        result.dayPlan.length !== travelDays ||
        result.dayPlan.some(
          (day: any) =>
            !Array.isArray(day?.cities) ||
            !day.cities.some((city: any) => String(city?.name || '').trim())
        )
      ) {
        throw new Error(
          'O roteiro retornou incompleto. Seus destinos foram preservados; tente gerar novamente.'
        )
      }
      const reconciliationStartedAt = performance.now()
      const reconciledDayPlan = await reconcileGeneratedCities(result.dayPlan)
      timings.reconciliationMs = Math.round(
        performance.now() - reconciliationStartedAt
      )
      console.info('[inspiration-draft] client reconciliation completed', {
        requestId,
        elapsedMs: timings.reconciliationMs
      })
      setDraft({
        ...formEmptyValues,
        ...result,
        dayPlan: reconciledDayPlan,
        made: 'false',
        tripDate: '',
        cities: []
      })
      setProgress(100)
      setStep(2)
      setCreationMode('manual')
      const editorScheduledAt = performance.now()
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          timings.editorRenderedMs = Math.round(performance.now() - startedAt)
          timings.postReconciliationMs = Math.round(
            performance.now() - editorScheduledAt
          )
          saveTelemetry('editor-rendered')
          console.info('[inspiration-draft] client editor rendered', {
            requestId,
            elapsedMs: timings.editorRenderedMs,
            timestamp: new Date().toISOString()
          })
        })
      )
      toast.success('Sua inspiração está pronta! Revise antes de salvar.')
    } catch (error: any) {
      const rawMessage = error?.response?.data?.message
      const serverMessage = Array.isArray(rawMessage)
        ? rawMessage.join(' ')
        : typeof rawMessage === 'string'
          ? rawMessage
          : ''
      const timedOut =
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ETIMEDOUT' ||
        /timeout/i.test(String(error?.message || ''))
      const message =
        serverMessage ||
        (timedOut
          ? 'A geração ultrapassou o tempo limite. Seus dados continuam preenchidos. Tente novamente mais tarde.'
          : error?.message?.startsWith('O roteiro retornou incompleto')
            ? error.message
            : 'Não foi possível concluir a inspiração. Seus destinos continuam preenchidos; tente novamente.')
      timings.failureKind =
        error?.response?.status === 429 || /limite diário/i.test(message)
          ? 'quota'
          : timedOut
            ? 'timeout'
            : 'other'
      saveTelemetry('failed')
      setGenerationError(message)
      toast.error(message, { autoClose: 8000 })
    } finally {
      console.info('[inspiration-draft] client request finished', {
        requestId,
        elapsedMs: Math.round(performance.now() - startedAt)
      })
      setGenerating(false)
    }
  }

  return (
    <PageContainer>
      {isSaving && (
        <SavingOverlay role="status" aria-live="polite" aria-busy="true">
          <SavingCard>
            <SavingSpinner />
            <strong>Salvando seu roteiro...</strong>
            <span>
              Estamos gravando dias, cidades e fotos. Não feche esta página.
            </span>
          </SavingCard>
        </SavingOverlay>
      )}
      {generating && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '32px 20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              width: 'min(540px,100%)',
              textAlign: 'center',
              color: '#222'
            }}
          >
            <h2
              style={{
                margin: '0 0 16px',
                fontSize: 'clamp(23px, 5vw, 32px)',
                lineHeight: 1.25
              }}
            >
              Sua próxima viagem está tomando forma
            </h2>
            <p
              style={{
                color: '#555',
                margin: '0 0 44px',
                fontSize: 17,
                lineHeight: 1.5
              }}
            >
              Estamos selecionando lugares reais e organizando seus dias.
            </p>
            <div
              role="progressbar"
              aria-label="Preparando sua inspiração"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress}
              aria-valuetext="Progresso estimado da preparação"
              style={{
                position: 'relative',
                height: 9,
                borderRadius: 9,
                background: '#f5dada',
                margin: '0 18px 0 8px'
              }}
            >
              <div
                style={{
                  width: progress + '%',
                  height: '100%',
                  background: '#d60000',
                  borderRadius: 9,
                  transition: 'width 500ms linear'
                }}
              />
              <img
                src={plannedSternaPin}
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: progress + '%',
                  top: '50%',
                  width: 26,
                  height: 26,
                  objectFit: 'contain',
                  transform: 'translate(-50%, -50%)',
                  transition: 'left 500ms linear',
                  pointerEvents: 'none'
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                color: '#777',
                fontSize: 12,
                margin: '15px 8px 0'
              }}
            >
              <strong style={{ color: '#d60000' }}>{progress}%</strong>
              <span>
                {progress < 45
                  ? 'Entendendo suas preferências'
                  : progress < 75
                    ? 'Selecionando lugares e experiências'
                    : progress < 100
                      ? 'Organizando e conferindo seu roteiro'
                      : 'Pronto'}
              </span>
            </div>
            <p
              style={{
                color: '#777',
                fontSize: 13,
                lineHeight: 1.6,
                marginTop: 28
              }}
            >
              Progresso estimado. A inspiração será exibida assim que estiver
              pronta.
            </p>
          </div>
        </div>
      )}
      <ContainerCreateItinerary>
        {creationMode !== 'manual' && (
          <section
            style={{
              width: 'min(720px, 94%)',
              margin: '48px auto',
              padding: 28,
              border: '1px solid #eee',
              borderRadius: 18
            }}
          >
            <h1>Como vamos começar sua próxima viagem?</h1>
            <p style={{ color: '#777', marginBottom: 24 }}>
              Monte do seu jeito ou deixe a Redsterna trazer inspiração.
            </p>
            {creationMode === 'choose' ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 16
                }}
              >
                <button
                  type="button"
                  onClick={() => setCreationMode('manual')}
                  style={{
                    padding: 25,
                    borderRadius: 14,
                    border: '1px solid #ddd',
                    background: '#fff',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                >
                  <strong>Já sei o que criar</strong>
                  <p>Monte seu roteiro normalmente.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setCreationMode('inspiration')}
                  style={{
                    padding: 25,
                    borderRadius: 14,
                    border: '1px solid #d60000',
                    background: '#fff7f7',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                >
                  <strong>Quero inspiração ✨</strong>
                  <p>Receba uma proposta editável.</p>
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                <label>
                  Cidades, atrações ou lugares que deseja conhecer
                  <input
                    value={destinations}
                    maxLength={300}
                    onChange={(e) => setDestinations(e.target.value)}
                    placeholder="Ex.: Castelo de Eltz, Paris ou Annecy"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: 12,
                      border: '1px solid #ddd',
                      borderRadius: 8
                    }}
                  />
                </label>
                <label>
                  Duração em dias
                  <input
                    type="number"
                    min={1}
                    max={14}
                    value={travelDays}
                    onChange={(e) => setTravelDays(Number(e.target.value))}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: 12,
                      border: '1px solid #ddd',
                      borderRadius: 8
                    }}
                  />
                </label>
                <label>
                  O que gosta de fazer?
                  <textarea
                    value={travelInterests}
                    maxLength={600}
                    onChange={(e) => setTravelInterests(e.target.value)}
                    placeholder="Gastronomia, cultura, natureza..."
                    rows={4}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: 12,
                      border: '1px solid #ddd',
                      borderRadius: 8
                    }}
                  />
                </label>
                {generationError && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    style={{
                      padding: '14px 16px',
                      border: '1px solid #c73838',
                      background: '#fff3f3',
                      borderRadius: 9,
                      color: '#8e1616',
                      lineHeight: 1.5
                    }}
                  >
                    <strong>Não foi possível concluir sua inspiração.</strong>
                    <p style={{ margin: '6px 0 0' }}>{generationError}</p>
                    <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                      Revise os destinos ou tente novamente. Nada foi apagado.
                    </p>
                  </div>
                )}
                <p style={{ color: '#777', fontSize: 12 }}>
                  Até 3 gerações por dia nesta fase de testes. Confira atrações
                  e horários.
                </p>
                <button
                  type="button"
                  disabled={generating}
                  onClick={generateDraft}
                  style={{
                    padding: 14,
                    border: 0,
                    borderRadius: 9,
                    background: '#d60000',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {generating
                    ? 'Preparando inspiração...'
                    : 'Criar minha inspiração'}
                </button>
                <button
                  type="button"
                  disabled={generating}
                  onClick={() => setCreationMode('choose')}
                >
                  Voltar
                </button>
              </div>
            )}
          </section>
        )}
        {creationMode === 'manual' && (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={async (values) => {
              if (isSaving) return

              const missing = getMissingRequiredFields(values)

              if (missing.length) {
                showMissingFieldsMessage(missing)
                return
              }

              if (
                isMadeItinerary(values.made) &&
                !isPastTripDate(values.tripDate)
              ) {
                toast.warning(
                  'Para um roteiro feito, a data da viagem precisa estar no passado.',
                  { theme: 'dark' }
                )
                return
              }

              const made = isMadeItinerary(values.made)
              const normalizedDayPlan = (values.dayPlan || []).map(
                (day: any, dayIndex: number) => ({
                  ...day,
                  dayNumber: dayIndex + 1,
                  date: made ? addDaysToDate(values.tripDate, dayIndex) : null,
                  cities: (day.cities || []).map(
                    (city: any, cityIndex: number) => ({
                      ...city,
                      dayNumber: dayIndex + 1,
                      order: cityIndex + 1,
                      rate: null,
                      accommodations: city.accommodations || [],
                      images: city.images || []
                    })
                  )
                })
              )

              setIsSaving(true)

              try {
                const created = await createItinerary({
                  ...values,
                  days: normalizedDayPlan.length,
                  cities: [],
                  dayPlan: normalizedDayPlan,
                  tripDate: made ? values.tripDate : '',
                  coverImage: values.coverImage || DEFAULT_ITINERARY_COVER
                })

                if (!created) {
                  setIsSaving(false)
                  return
                }

                window.localStorage.setItem('itinerary', '')

                await Promise.all([
                  queryClient.invalidateQueries({ queryKey: ['itineraries'] }),
                  queryClient.invalidateQueries({
                    queryKey: ['itinerariesUser']
                  }),
                  queryClient.invalidateQueries({ queryKey: ['MapMarkers'] }),
                  queryClient.invalidateQueries({
                    queryKey: ['destination-catalog-summary']
                  }),
                  queryClient.invalidateQueries({
                    queryKey: ['destination-catalog-state']
                  }),
                  queryClient.invalidateQueries({
                    queryKey: ['AllItineraries']
                  })
                ])

                history.push('/minha-conta')
              } catch {
                setIsSaving(false)
              }
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue
            }) => {
              const coverPreview = values.coverImage || DEFAULT_ITINERARY_COVER

              const goFromBasicInfo = () => {
                const missing: string[] = []
                if (!values.title?.trim()) missing.push('título da viagem')
                if (isMadeItinerary(values.made) && !values.tripDate) {
                  missing.push('data da viagem')
                }

                if (missing.length) {
                  showMissingFieldsMessage(missing)
                  return
                }

                if (
                  isMadeItinerary(values.made) &&
                  !isPastTripDate(values.tripDate)
                ) {
                  toast.warning(
                    'Para um roteiro feito, a data da viagem precisa estar no passado.',
                    { theme: 'dark' }
                  )
                  return
                }

                nextStep()
              }

              const goFromCities = () => {
                const dayPlan = Array.isArray(values.dayPlan)
                  ? values.dayPlan
                  : []

                if (dayPlan.length !== Number(values.days)) {
                  showMissingFieldsMessage(['a estrutura completa dos dias'])
                  return
                }

                const invalidDayIndex = dayPlan.findIndex(
                  (day: any) =>
                    !(day?.cities || []).some((city: any) =>
                      String(city?.name || '').trim()
                    )
                )

                if (invalidDayIndex >= 0) {
                  showMissingFieldsMessage([
                    `ao menos uma cidade no Dia ${invalidDayIndex + 1}`
                  ])
                  return
                }

                nextStep()
              }

              return (
                <>
                  <CoverCreateItinerary>
                    <CoverIcon src={coverPreview} alt="Capa do roteiro" />
                    <CoverOverlay>
                      <div>
                        <CoverTitle>Capa do seu roteiro</CoverTitle>
                        <CoverHint>
                          Escolha uma imagem que represente a viagem. Se
                          preferir, a RedSterna usa esta capa padrão
                          automaticamente.
                        </CoverHint>
                      </div>

                      <CoverActions>
                        <CoverButton htmlFor="itinerary-cover">
                          {isUploadingCover
                            ? 'Enviando...'
                            : values.coverImage
                              ? 'Trocar capa'
                              : 'Escolher capa'}
                        </CoverButton>
                        <CoverFileInput
                          id="itinerary-cover"
                          accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.avif,.heic,.heif"
                          type="file"
                          disabled={isUploadingCover}
                          onChange={async (event) => {
                            const file = event.target.files?.[0]
                            if (!file) return

                            setIsUploadingCover(true)
                            try {
                              const uploadedCover =
                                await uploadFileCallback(file)
                              await setFieldValue('coverImage', uploadedCover)
                            } catch (error: any) {
                              const apiMessage = Array.isArray(
                                error?.response?.data?.message
                              )
                                ? error.response.data.message.join(', ')
                                : error?.response?.data?.message

                              toast.error(
                                apiMessage ||
                                  'Não foi possível enviar a capa. Tente novamente.'
                              )
                            } finally {
                              setIsUploadingCover(false)
                              event.target.value = ''
                            }
                          }}
                        />

                        {values.coverImage && (
                          <CoverRemoveButton
                            type="button"
                            onClick={() => setFieldValue('coverImage', '')}
                          >
                            Usar capa padrão
                          </CoverRemoveButton>
                        )}
                      </CoverActions>
                    </CoverOverlay>
                  </CoverCreateItinerary>

                  <FormCreateItinerary onSubmit={handleSubmit}>
                    <FormContainerCreateItinerary>
                      <PainelFace
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        values={values}
                        setFieldValue={setFieldValue}
                        className={step === 1 ? '' : 'hidden'}
                        nextStep={goFromBasicInfo}
                      />

                      <PainelCities
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        values={values}
                        setFieldValue={setFieldValue}
                        className={step === 2 ? '' : 'hidden'}
                        nextStep={goFromCities}
                        prevStep={prevStep}
                      />

                      <PainelDescription
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        values={values}
                        setFieldValue={setFieldValue}
                        className={step === 3 ? '' : 'hidden'}
                        prevStep={prevStep}
                      />
                    </FormContainerCreateItinerary>
                    <FormikObserver />
                  </FormCreateItinerary>
                </>
              )
            }}
          </Formik>
        )}
      </ContainerCreateItinerary>
    </PageContainer>
  )
}

export default CreateItinerary
